import { Routes } from '@angular/router';
import { LoginComponent } from './features/auth/login.component';
import { AuthLayoutComponent } from './features/auth/components/auth-layout/auth-layout.component';
import { AppLayoutComponent } from './core/components/app-layout/app-layout.component';
import { LAYOUT_CONFIG } from './core/components/app-layout/layout-config';
import { adminLayoutConfig } from './features/admin/layout/admin-layout.config';
import { customerLayoutConfig } from './features/customer/layout/customer-layout.config';
import { partnerLayoutConfig } from './features/partner/layout/partner-layout.config';
import { partnerPortalGuard } from './core/shared/partner-portal.guard';
import { DashboardPage } from './features/admin/dashboard/dashboard.page';
import { UsersPage } from './features/admin/users/users.page';
import { AdvisoriesPage } from './features/admin/advisories/advisories.page';
import { ChartsPage } from './features/admin/charts/charts.page';
import { SyslogPage } from './features/admin/syslog/syslog.page';
import { OrganizationsPage } from './features/admin/organizations/organizations.page';
import { UpdatePasswordComponent } from './features/auth/components/update-password/update-password.component';
import { ResetPasswordComponent } from './features/auth/components/reset-password/reset-password.component';
import { ForgotPasswordComponent } from './features/auth/components/forgot-password/forgot-password.component';
import { EnterMfaComponent } from './features/auth/components/enter-mfa/enter-mfa.component';
import { SetupMfaComponent } from './features/auth/components/setup-mfa/setup-mfa.component';
import { GeneralComponent } from './features/admin/organizations/components/general/general.component';
import { SoarRulesComponent } from './features/admin/organizations/components/soar-rules/soar-rules.component';
import { SocCommsComponent } from './features/admin/organizations/components/soc-comms/soc-comms.component';
import { AnalyticsComponent } from './features/customer/analytics/analytics.component';
import { ReportingV2Page } from './features/customer/reporting-v2/reporting-v2.page';
import { DownloadComponent } from './features/customer/reporting-v2/components/download/download.component';
import { CustomerDashboardComponent } from './features/customer/customer-dashboard/customer-dashboard.component';
import { CustomerSettingsPage } from './features/customer/customer-settings/customer-settings.component';
import { NewAdvisoryComponent } from './features/admin/advisories/new-advisory/new-advisory.component';
import { ManagerPage } from './features/admin/manager/manager.page';
import { DetectionSettingsComponent } from './features/admin/organizations/components/detection-settings/detection-settings.component';
import { UnauthorizedPage } from './features/auth/unauthorized/unauthorized.page';
import { cyflareAdminGuard } from './core/shared/cyflare-admin.guard';
import { adminRouteGuard } from './core/shared/admin-route.guard';
import { legacyReportingRedirectGuard } from './core/shared/legacy-reporting-redirect.guard';
import { customerRouteGuard } from './core/shared/customer-route.guard';
import { mspRouteGuard } from './core/shared/msp-route.guard';
import { reportingV2RouteGuard } from './core/shared/reporting-v2-route.guard';
import { featureFlagGuard } from './core/shared/feature-flag.guard';
import { AdvisoryPage } from './features/customer/advisory/advisory.page';
import { SoarOperationsComponent } from './features/customer/soar-operations/soar-operations.component';
import { RedirectLandingComponent } from './features/auth/components/redirect-landing/redirect-landing.component';
import { DetectionsBoardComponent } from './features/customer/detections-board/detections-board.page';
import { RoadmapComponent } from './features/admin/roadmap/roadmap.component';
import { CaseDashboardComponent } from './features/customer/case-dashboard/case-dashboard.component';
import { TicketsLogPage } from './features/customer/tickets-log/tickets-log.page';
import { CasesComponent } from './features/customer/cases/cases.component';
import { IntegrationsComponent as AdminIntegrationsComponent } from './features/admin/organizations/components/integrations/integrations.component';
import { CustomersComponent } from './features/admin/organizations/components/integrations/components/connectwise/customers/customers.component';
import { MitreAttackComponent } from './features/customer/mitreattack/mitreattack.component';
import { routes as integrationsRoutes } from './features/admin/organizations/components/integrations/integrations-routing.module';
import { AlertPreferencesPage } from './features/customer/alert-preferences/alert-preferences.page';
import { CustomerSocCommsPage } from './features/customer/customer-soc-comms/customer-soc-comms.page';
import { SocTuningPage } from './features/customer/soc-tuning/soc-tuning.page';
import { WhiteLabelComponent } from './features/admin/organizations/components/white-label/white-label.component';
import { FeatureFlagsComponent } from './features/admin/organizations/components/feature-flags/feature-flags.component';
import { PartnerAnalyticsPage } from './features/customer/msp-portal/analytics/partner-analytics.page';
import { CustomerOnboardingComponent } from './features/customer/msp-portal/customers/components/customer-onboarding/customer-onboarding.component';
import { IntegrationsComponent as CustomerIntegrationsComponent } from './features/customer/integrations/integrations.component';
import { PagingComponent } from './features/admin/organizations/components/paging/paging.component';
import { EsComponent } from './features/customer/analytics/components/es/es.component';
import { ServicesComponent } from './features/admin/organizations/components/services/services.component';
import { CustomersPage } from './features/customer/msp-portal/customers/customers.page';
import { CustomerDetailsComponent } from './features/customer/msp-portal/customers/components/customer-details';
import { SocComponent } from './features/customer/analytics/components/soc/soc.component';
import { PolicyGapAnalysisComponent } from './features/customer/policy-gap-analysis/policy-gap-analysis.component';
import { IdentitySecurityComponent } from './features/customer/identity-security/identity-security.page';

export const routes: Routes = [
  { path: '', redirectTo: 'auth/login', pathMatch: 'full' },
  {
    path: 'auth',
    component: AuthLayoutComponent,
    children: [
      { path: 'login', component: LoginComponent },
      { path: 'update-password', component: UpdatePasswordComponent },
      { path: 'reset-password', component: ResetPasswordComponent },
      { path: 'forgot-password', component: ForgotPasswordComponent },
      { path: 'enter-mfa', component: EnterMfaComponent },
      { path: 'setup-mfa', component: SetupMfaComponent },
      { path: 'redirect-landing', component: RedirectLandingComponent },
    ],
  },
  {
    path: 'admin',
    component: AppLayoutComponent,
    providers: [{ provide: LAYOUT_CONFIG, useValue: adminLayoutConfig }],
    children: [
      { path: 'dashboard', component: DashboardPage, canActivate: [adminRouteGuard], data: { title: 'Dashboard' } },
      { path: 'users', component: UsersPage, canActivate: [adminRouteGuard], data: { title: 'Users' } },
      { path: 'advisories', component: AdvisoriesPage, canActivate: [adminRouteGuard], data: { title: 'Advisories' } },
      { path: 'charts', component: ChartsPage, canActivate: [adminRouteGuard], data: { title: 'Charts' } },
      { path: 'syslog', component: SyslogPage, canActivate: [adminRouteGuard], data: { title: 'System Log' } },
      {
        path: 'organizations',
        component: OrganizationsPage,
        canActivate: [adminRouteGuard],
        data: { title: 'Organizations' },
      },
      {
        path: 'organizations/general',
        component: GeneralComponent,
        canActivate: [adminRouteGuard],
        data: { title: 'General - ${org.name}', requiresOrgName: true },
      },
      {
        path: 'organizations/soar-rules',
        component: SoarRulesComponent,
        canActivate: [adminRouteGuard],
        data: { title: 'Soar Rules - ${org.name}', requiresOrgName: true },
      },
      {
        path: 'organizations/soc-comms',
        component: SocCommsComponent,
        canActivate: [adminRouteGuard],
        data: { title: 'SOC Comms - ${org.name}', requiresOrgName: true },
      },
      {
        path: 'organizations/detection-settings',
        component: DetectionSettingsComponent,
        canActivate: [adminRouteGuard],
        data: { title: 'Detection Settings - ${org.name}', requiresOrgName: true },
      },
      {
        path: 'organizations/integrations',
        component: AdminIntegrationsComponent,
        canActivate: [adminRouteGuard],
        data: { title: 'Integrations - ${org.name}', requiresOrgName: true },
        children: integrationsRoutes,
      },
      {
        path: 'organizations/integrations/:integration/customers',
        component: CustomersComponent,
        canActivate: [adminRouteGuard],
        data: { title: 'Integration - Customers' },
      },
      {
        path: 'advisories/new-advisory',
        component: NewAdvisoryComponent,
        canActivate: [adminRouteGuard],
        data: { title: 'New Advisory' },
      },
      {
        path: 'manager',
        component: ManagerPage,
        canActivate: [adminRouteGuard],
        data: { title: 'Detections Manager' },
      },
      { path: 'roadmap', component: RoadmapComponent, canActivate: [adminRouteGuard], data: { title: 'Roadmap' } },
      {
        path: 'organizations/whitelabel',
        component: WhiteLabelComponent,
        canActivate: [adminRouteGuard],
        data: { title: 'White Label - ${org.name}', requiresOrgName: true },
      },
      {
        path: 'organizations/feature-flags',
        component: FeatureFlagsComponent,
        canActivate: [adminRouteGuard],
        data: { title: 'Feature Flags - ${org.name}', requiresOrgName: true },
      },
      {
        path: 'organizations/paging',
        component: PagingComponent,
        canActivate: [adminRouteGuard],
        data: { title: 'Paging - ${org.name}', requiresOrgName: true },
      },
      {
        path: 'organizations/services',
        component: ServicesComponent,
        canActivate: [adminRouteGuard],
        data: { title: 'Services - ${org.name}', requiresOrgName: true },
      },
      {
        path: 'billing/products',
        loadComponent: () =>
          import('./features/admin/billing/products/products.page').then((m) => m.ProductsPage),
        canActivate: [adminRouteGuard],
        data: { title: 'Product Catalog' },
      },
      {
        path: 'billing/dimension-links',
        loadComponent: () =>
          import('./features/admin/billing/dimension-links/dimension-links.page').then(
            (m) => m.DimensionLinksPage
          ),
        canActivate: [adminRouteGuard],
        data: { title: 'Dimension Links' },
      },
      {
        path: 'casi-knowledge',
        loadComponent: () =>
          import('./features/admin/casi-knowledge/casi-knowledge.page').then((m) => m.CasiKnowledgePage),
        canActivate: [adminRouteGuard, cyflareAdminGuard],
        data: { title: 'CASI Knowledge' },
      },
    ],
  },
  {
    path: 'customer',
    component: AppLayoutComponent,
    providers: [{ provide: LAYOUT_CONFIG, useValue: customerLayoutConfig }],
    children: [
      { path: 'customer-dashboard', component: CustomerDashboardComponent, canActivate: [customerRouteGuard] },
      { path: 'customer-settings', component: CustomerSettingsPage, canActivate: [customerRouteGuard] },
      { path: 'analytics', component: AnalyticsComponent, canActivate: [customerRouteGuard] },
      {
        // No featureFlagGuard here: the flag still gates this route, through
        // FLAG_GATED_ROUTES and the redirect effect in AppLayoutComponent, which is
        // the same way /customer/asset-intelligence is gated. The guard was redundant with it
        // and deadlocked on a hard refresh - it waits for flags that only
        // AppLayoutComponent loads, and the layout cannot mount until the guard lets
        // the navigation through, so the page stayed blank.
        path: 'itdr',
        component: IdentitySecurityComponent,
        canActivate: [customerRouteGuard],
        data: { title: 'Identity Security' },
      },
      {
        path: 'analytics-v1',
        component: SocComponent,
        canActivate: [customerRouteGuard],
        data: { title: 'SOC Analytics V1 (Legacy)' },
      },
      {
        path: 'msp-portal',
        children: [
          {
            path: 'partner-analytics',
            component: PartnerAnalyticsPage,
            canActivate: [mspRouteGuard],
            data: { title: 'MSP Analytics' },
          },
          {
            path: 'customers/customer-onboarding',
            component: CustomerOnboardingComponent,
            canActivate: [mspRouteGuard],
            data: { title: 'Customer Onboarding' },
          },
          {
            path: 'customers',
            component: CustomersPage,
            canActivate: [mspRouteGuard],
            data: { title: 'Customers' },
          },
          {
            path: 'customers/customer-details',
            component: CustomerDetailsComponent,
            canActivate: [mspRouteGuard],
            data: { title: 'Customer Details' },
          },
          // Add more MSP portal routes here as needed
        ],
      },
      { path: 'soar-operations', component: SoarOperationsComponent, canActivate: [customerRouteGuard] },
      { path: 'reporting', canActivate: [legacyReportingRedirectGuard], children: [] },
      {
        path: 'reporting-v2/download',
        component: DownloadComponent,
        canActivate: [reportingV2RouteGuard],
        data: { title: 'Download Report' },
      },
      {
        path: 'reporting-v2',
        component: ReportingV2Page,
        canActivate: [reportingV2RouteGuard],
        data: { title: 'Reporting Center' },
      },
      { path: 'mitreattack', component: MitreAttackComponent, canActivate: [customerRouteGuard] },
      {
        path: 'cases',
        component: CasesComponent,
        canActivate: [customerRouteGuard, featureFlagGuard('cases_v2')],
        data: { title: 'Cases' },
      },
      {
        path: 'tickets-log',
        component: TicketsLogPage,
        canActivate: [customerRouteGuard],
        data: { title: 'Tickets Log' },
      },
      { path: 'advisory', component: AdvisoryPage, canActivate: [customerRouteGuard] },
      { path: 'unauthorized', component: UnauthorizedPage },
      {
        path: 'partner-no-access',
        loadComponent: () =>
          import('./features/partner/pages/no-access/partner-no-access.page').then((m) => m.PartnerNoAccessPage),
        data: { title: 'Partner Portal' },
      },
      {
        path: 'organizations',
        component: OrganizationsPage,
        canActivate: [customerRouteGuard],
        data: { title: 'Organizations' },
      },
      {
        path: 'organizations/general',
        component: GeneralComponent,
        canActivate: [customerRouteGuard],
        data: { title: 'General - ${org.name}', requiresOrgName: true },
      },
      {
        path: 'organizations/soar-rules',
        component: SoarRulesComponent,
        canActivate: [customerRouteGuard],
        data: { title: 'Soar Rules - ${org.name}', requiresOrgName: true },
      },
      {
        path: 'organizations/soc-comms',
        component: SocCommsComponent,
        canActivate: [customerRouteGuard],
        data: { title: 'SOC Comms - ${org.name}', requiresOrgName: true },
      },
      {
        path: 'organizations/detection-settings',
        component: DetectionSettingsComponent,
        canActivate: [customerRouteGuard],
        data: { title: 'Detection Settings - ${org.name}', requiresOrgName: true },
      },
      { path: 'detections-board', component: DetectionsBoardComponent, canActivate: [customerRouteGuard] },
      { path: 'case-dashboard', component: CaseDashboardComponent, canActivate: [adminRouteGuard] },
      {
        path: 'organizations/integrations',
        component: AdminIntegrationsComponent,
        canActivate: [customerRouteGuard],
        data: { title: 'Integrations - ${org.name}', requiresOrgName: true },
      },
      { path: 'alert-preferences', component: AlertPreferencesPage, canActivate: [customerRouteGuard] },
      { path: 'soc-tuning', component: SocTuningPage, canActivate: [customerRouteGuard] },
      { path: 'customer-soc-comms', component: CustomerSocCommsPage, canActivate: [customerRouteGuard] },
      {
        path: 'integrations',
        component: CustomerIntegrationsComponent,
        canActivate: [customerRouteGuard],
        data: { title: 'Integrations' },
      },
      { path: 'es', component: EsComponent, canActivate: [customerRouteGuard] },
      {
        path: 'asset-intelligence',
        loadComponent: () => import('./features/customer/inventory/inventory.page').then((m) => m.InventoryPage),
        canActivate: [customerRouteGuard],
        data: { title: 'Asset Intelligence' },
      },
      {
        // PROTOTYPE — AIM pivot exploration. Not linked from the nav.
        path: 'asset-intelligence/pivot',
        loadComponent: () => import('./features/customer/inventory/pivot/pivot.page').then((m) => m.PivotPage),
        canActivate: [customerRouteGuard],
        data: { title: 'Entity Pivot (Prototype)' },
      },
      {
        path: 'asset-intelligence/review',
        loadComponent: () => import('./features/customer/inventory/review/review-queue.page').then((m) => m.ReviewQueuePage),
        canActivate: [customerRouteGuard],
        data: { title: 'Identity Review' },
      },
      {
        path: 'asset-intelligence/exceptions',
        loadComponent: () => import('./features/customer/inventory/exceptions/exceptions.page').then((m) => m.ExceptionsPage),
        canActivate: [customerRouteGuard],
        data: { title: 'Insight Exceptions' },
      },
      {
        /**
         * Legacy path from before Entity Inventory became Asset Intelligence.
         *
         * Prefix match, so the router carries the remaining segments across:
         * /customer/inventory/review lands on /customer/asset-intelligence/review.
         * Kept because this URL has been shipped — it lives in bookmarks and in
         * links sent out of the product — and a rename is not a reason to break
         * someone's saved link.
         *
         * MUST stay below the asset-intelligence routes above; a prefix redirect
         * declared earlier would swallow them.
         */
        path: 'inventory',
        redirectTo: 'asset-intelligence',
      },
      {
        path: 'policy-gap-analysis',
        component: PolicyGapAnalysisComponent,
        canActivate: [customerRouteGuard, featureFlagGuard('policy_analysis')],
        data: { title: 'Policy Gap Analysis' },
      },
      {
        path: 'data-house',
        loadComponent: () =>
          import('./features/customer/data-house/data-house.page').then((m) => m.DataHousePage),
        canActivate: [customerRouteGuard, featureFlagGuard('data_house')],
        data: { title: 'Data House' },
      },
      {
        path: 'organizations/services',
        component: ServicesComponent,
        canActivate: [customerRouteGuard],
        data: { title: 'Services - ${org.name}', requiresOrgName: true },
      },
    ],
  },
  {
    path: 'partner',
    component: AppLayoutComponent,
    providers: [{ provide: LAYOUT_CONFIG, useValue: partnerLayoutConfig }],
    canActivate: [partnerPortalGuard],
    children: [
      {
        path: 'home',
        loadComponent: () => import('./features/partner/pages/home/partner-home.page').then((m) => m.PartnerHomePage),
        data: { title: 'Partner Portal' },
      },
      { path: '', pathMatch: 'full', redirectTo: 'home' },
    ],
  },
  { path: '**', redirectTo: 'customer/unauthorized' },
];
